/* eslint-disable max-lines */
/* eslint-disable local/no-small-variable-names */
/* eslint-disable max-lines-per-function */
import {
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderPlaceholderProps,
  Slate,
  withReact,
} from 'slate-react';

import {
  Descendant,
  Editor,
  Node,
  Range,
  Element as SlateElement,
  Text,
  Transforms,
  createEditor,
} from 'slate';
import { withHistory } from 'slate-history';

import { cn } from '@/lib/utils';

import { ContextSelectionType } from '@/components/chat/ContextSelectionContext';

import { ContextInlineToken } from './ContextInlineToken';

export type ContextToken = {
  id: string;
  label: string;
  type: ContextSelectionType;
};

type ContextTokenElement = {
  type: 'context_token';
  token: ContextToken;
  children: [{ text: '' }];
};

type DescendantWithType = Descendant & { type: string };

const createEmptyValue = (): DescendantWithType[] => [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const createValueFromText = (text: string): DescendantWithType[] => [
  {
    type: 'paragraph',
    children: [{ text }],
  },
];

const serializeNode = (node: DescendantWithType): string => {
  if (Text.isText(node)) {
    return node.text;
  }

  if (SlateElement.isElement(node)) {
    if (node.type === 'context_token') {
      return node.token.label;
    }

    return node.children.map(serializeNode).join('');
  }

  return '';
};

const serialize = (nodes: Descendant[]) => nodes.map(serializeNode).join('\n');

const withContextTokens = (editor: Editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element: SlateElement) =>
    element.type === 'context_token' ? true : isInline(element);

  editor.isVoid = (element: SlateElement) =>
    element.type === 'context_token' ? true : isVoid(element);

  return editor;
};

const getAtQuery = (editor: Editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return null;
  }

  const [blockEntry] = Editor.nodes(editor, {
    match: (node: Node) => Editor.isBlock(editor, node),
  });

  if (!blockEntry) {
    return null;
  }

  const [, blockPath] = blockEntry;
  const blockStart = Editor.start(editor, blockPath);
  const range = { anchor: blockStart, focus: selection.anchor };
  const text = Editor.string(editor, range);
  const match = text.match(/(?:^|\s)(@\S*)$/);

  if (!match) {
    return null;
  }

  return match[1].slice(1);
};

const getAtRange = (editor: Editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return null;
  }

  const [blockEntry] = Editor.nodes(editor, {
    match: (node: Node) => Editor.isBlock(editor, node),
  });

  if (!blockEntry) {
    return null;
  }

  const [, blockPath] = blockEntry;
  const blockStart = Editor.start(editor, blockPath);
  const range = { anchor: blockStart, focus: selection.anchor };
  const text = Editor.string(editor, range);
  const match = text.match(/(?:^|\s)(@\S*)$/);

  if (!match) {
    return null;
  }

  const matchText = match[1];
  const matchStart = Editor.before(editor, selection, {
    unit: 'character',
    distance: matchText.length,
  });

  if (!matchStart) {
    return null;
  }

  return { anchor: matchStart, focus: selection.anchor };
};

const RenderPlaceholder = ({
  attributes,
  children,
}: RenderPlaceholderProps) => (
  <span
    {...attributes}
    style={{
      ...attributes.style,
      pointerEvents: 'none',
      opacity: 0.4,
      top: '1rem',
      left: '1rem',
      display: 'flex',
      alignItems: 'center',
      height: '1.5em',
      lineHeight: '1em',
    }}
  >
    {children}
  </span>
);

interface SlateMessageInputHandle {
  clearPendingAtQuery: () => void;
  focus: () => void;
  clear: () => void;
  getPlainText: () => string;
  insertContextToken: (token: ContextToken) => void;
}

interface SlateMessageInputProps {
  value: string;
  onChangeValue: (value: string) => void;
  onSubmit: () => void;
  onAtQueryChange?: (query: string | null) => void;
  onKeyDown?: (
    event: KeyboardEvent<HTMLElement>,
    onEnterWhenClosed: () => void
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  fixedHeight?: boolean;
  ariaExpanded?: boolean;
}

export const SlateMessageInput = forwardRef<
  SlateMessageInputHandle,
  SlateMessageInputProps
>(
  (
    {
      value = '',
      onChangeValue,
      onSubmit,
      onAtQueryChange,
      onKeyDown,
      placeholder,
      disabled,
      fixedHeight,
      ariaExpanded,
    },
    ref
  ) => {
    const editor = useMemo<ReactEditor>(() => {
      const base = createEditor();
      const reactEditor = withReact(base) as ReactEditor;
      const historyEditor = withHistory(reactEditor);
      return withContextTokens(historyEditor) as ReactEditor;
    }, []);

    const [editorValue, setEditorValue] = useState<Descendant[]>(() =>
      value ? createValueFromText(value) : createEmptyValue()
    );
    const lastSelectionRef = useRef<Range | null>(null);

    const resetEditor = useCallback(
      (nextValue: Descendant[]) => {
        Editor.withoutNormalizing(editor, () => {
          for (let index = editor.children.length - 1; index >= 0; index -= 1) {
            Transforms.removeNodes(editor, { at: [index] });
          }

          Transforms.insertNodes(editor, nextValue, { at: [0] });
          const nextSelection = Editor.end(editor, []);
          Transforms.select(editor, nextSelection);
          lastSelectionRef.current = editor.selection;
        });
        setEditorValue(nextValue);
      },
      [editor]
    );

    useEffect(() => {
      const serialized = serialize(editor.children as Descendant[]);
      if (value === serialized) {
        return;
      }

      const nextValue = value ? createValueFromText(value) : createEmptyValue();
      resetEditor(nextValue);
    }, [value, resetEditor, editor]);

    useImperativeHandle(
      ref,
      () => ({
        clearPendingAtQuery: () => {
          if (lastSelectionRef.current) {
            Transforms.select(editor, lastSelectionRef.current);
          }

          if (!editor.selection) {
            Transforms.select(editor, Editor.end(editor, []));
          }

          const atRange = getAtRange(editor);
          if (atRange) {
            Transforms.select(editor, atRange);
            Transforms.delete(editor);
          }

          lastSelectionRef.current = editor.selection;
          ReactEditor.focus(editor);
        },
        focus: () => {
          ReactEditor.focus(editor);
        },
        clear: () => {
          resetEditor(createEmptyValue());
        },
        getPlainText: () => serialize(editor.children as Descendant[]),
        insertContextToken: (token: ContextToken) => {
          if (lastSelectionRef.current) {
            Transforms.select(editor, lastSelectionRef.current);
          }

          if (!editor.selection) {
            Transforms.select(editor, Editor.end(editor, []));
          }

          const atRange = getAtRange(editor);
          if (atRange) {
            Transforms.select(editor, atRange);
            Transforms.delete(editor);
          }

          const tokenNode: ContextTokenElement = {
            type: 'context_token',
            token,
            children: [{ text: '' }],
          };

          Transforms.insertNodes(editor, tokenNode);
          Transforms.insertText(editor, ' ');
          lastSelectionRef.current = editor.selection;
          ReactEditor.focus(editor);
        },
      }),
      [editor, resetEditor]
    );

    const renderElement = useCallback((props: RenderElementProps) => {
      const { element, attributes, children } = props;

      if (element.type === 'context_token') {
        const tokenElement = element as ContextTokenElement;

        return (
          <ContextInlineToken
            label={tokenElement.token.label}
            type={tokenElement.token.type}
            slateAttributes={attributes}
          >
            {children}
          </ContextInlineToken>
        );
      }

      return (
        <p {...attributes} className="whitespace-pre-wrap min-w-0">
          {children}
        </p>
      );
    }, []);

    const handleChange = useCallback(
      (nextValue: Descendant[]) => {
        setEditorValue(nextValue);
        onChangeValue(serialize(nextValue));
        if (editor.selection) {
          lastSelectionRef.current = editor.selection;
        }

        if (onAtQueryChange) {
          const query = getAtQuery(editor);
          onAtQueryChange(query);
        }
      },
      [editor, onAtQueryChange, onChangeValue]
    );

    const handleSelect = useCallback(() => {
      if (editor.selection) {
        lastSelectionRef.current = editor.selection;
      }
    }, [editor]);

    const handleBlur = useCallback(() => {
      if (editor.selection) {
        lastSelectionRef.current = editor.selection;
      }
    }, [editor]);

    const handleFocus = useCallback(() => {
      if (lastSelectionRef.current) {
        Transforms.select(editor, lastSelectionRef.current);
        return;
      }

      Transforms.select(editor, Editor.end(editor, []));
      lastSelectionRef.current = editor.selection;
    }, [editor]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !ariaExpanded) {
          event.preventDefault();
          onSubmit();
          return;
        }

        onKeyDown?.(event, onSubmit);
        if (event.defaultPrevented) {
          return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          onSubmit();
        }
      },
      [ariaExpanded, onKeyDown, onSubmit]
    );

    return (
      <div className="min-w-0 flex-1" data-testid="stella-message-input">
        <Slate
          editor={editor}
          initialValue={editorValue}
          onChange={handleChange}
        >
          <Editable
            role="textbox"
            aria-expanded={ariaExpanded}
            readOnly={disabled}
            placeholder={placeholder}
            renderPlaceholder={RenderPlaceholder}
            onKeyDown={handleKeyDown}
            onSelect={handleSelect}
            onBlur={handleBlur}
            onFocus={handleFocus}
            renderElement={renderElement}
            className={cn(
              'w-full resize-none border-0 p-4 shadow-none',
              'focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
              'focus-visible:ring-offset-0 focus:shadow-none focus-visible:shadow-none',
              'overflow-y-auto overflow-x-hidden',
              {
                'max-h-[110px]': fixedHeight,
                'max-h-[200px]': !fixedHeight,
              }
            )}
          />
        </Slate>
      </div>
    );
  }
);

SlateMessageInput.displayName = 'SlateMessageInput';
